import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useApi } from '../api/useApi';
import { useThermalZone } from '../Comfort/useThermalZone';
import {
  useCampusMeta,
  useCampusControlModes,
  useCampusWeather,
} from '../../context/CampusContextProvider';
import { useIsReadOnly } from '../../context/UserContextProvider';
import { useModal } from '../modal/useModal';

import { setThermalZoneStatus, getHvacMode } from '../../utilities/ProcessRealtimeZoneHelper';

import { CONTROL_MODE } from '../../config/control';
import { ACTIVITY } from '../../config/activity';
import { ICON } from '../../config/icons';
import ComfortIcon from '../../components/icons/ComfortIcon';

import { TempSettingsLockup } from '../../components/TempSettingsLockup/TempSettingsLockup';
import { SelectOption } from '../../components/SelectOption/SelectOption';
import { ThermalZoneStatusPill } from '../../components/ThermalZoneStatus/ThermalZoneStatusPill';
import { ActivityDisplay } from '../../components/ActivityDisplay/ActivityDisplay';
import { DataCard } from '../../components/DataCard/DataCard';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { Breadcrumb } from '../../components/Breadcrumb/Breadcrumb';
import { ToggleWithIcon } from '../../components/ToggleWithIcon/ToggleWithIcon';
import Loading from '../../components/Loading';
import { PerformanceChart } from '../Charts/PerformanceChart';
import { performanceChartConfiguration } from '../Charts/configs/performance-chart';
import { PermissionBlock } from '../permissions/PermissionBlock';

import './ComfortDetail.scss';

const ComfortDetail = ({ showMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const isReadOnly = useIsReadOnly();
  const { fetchDataJson, fetchJson } = useApi();

  const campusMeta = useCampusMeta();
  const campusWeather = useCampusWeather();

  const thermalZoneControlModes = useCampusControlModes();

  const { openModal } = useModal();
  const errorMessageRef = useRef(null);

  const {
    updateThermalZoneSchedule,
    filterActiveZoneEvents,
    handleLock,
    handleManageOptimized,
    handleCancelOverride,
    getActiveEventControlModes,
  } = useThermalZone();
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [controlModes, setControlModes] = useState([]);
  const [controlModeOverrides, setControlModeOverrides] = useState([]);
  const [controlModeEvents, setControlModeEvents] = useState([]);
  const [hasRemoteOverride, setHasRemoteOverride] = useState(false);

  const [isKeypadLocked, setIsKeypadLocked] = useState(null);
  const [isManaged, setIsManaged] = useState(null);
  const [isOffline, setIsOffline] = useState(null);
  const [showLoading, setShowLoading] = useState({ message: '', isLoading: false });

  // get router params
  const campusId = params.campusId;
  const buildingId = params.buildingId;
  const zoneId = params.zoneId;
  const campusName = campusMeta?.campusName || 'Campus';

  // get zone data
  const {
    data: zoneData,
    isLoading,
    error,
    refetch: refetchZoneData,
  } = useQuery({
    queryKey: ['zoneData', zoneId],
    queryFn: () => fetchDataJson(`/ahiapi/${zoneId}/thermalZone`),
    refetchInterval: 5 * 1000, // 5 seconds
  });

  // get zone event data
  const {
    data: zoneEventData,
    isLoading: zoneEventDataIsLoading,
    error: zoneEventDataError,
    refetch: refetchZoneEventData,
  } = useQuery({
    queryKey: ['zoneEventData', zoneId],
    queryFn: () => fetchDataJson(`/ahiapi/${zoneId}/thermalZone/events`),
    refetchInterval: 15 * 1000, // 15 seconds
  });

  // get campus event data
  // const {
  //   data: campusEventData,
  //   isLoading: campusEventDataIsLoading,
  //   error: campusEventDataError,
  // } = useQuery({
  //   queryKey: ['campusEventData', campusId],
  //   queryFn: () => fetchDataJson(`/ahiapi/${campusId}/events`),
  //   // refetchInterval: 5 * 1000, // 5 seconds
  // });

  // get campus schedule data
  const {
    data: scheduleData,
    isLoading: scheduleDataIsLoading,
    error: scheduleDataError,
  } = useQuery({
    queryKey: ['scheduleData', campusId],
    queryFn: () => fetchDataJson(`/ahiapi/${campusId}/schedules/${`small`}`),
    refetchInterval: 15 * 1000, // 15 seconds
  });

  const {
    longThermalZoneDisplayName: zoneName,
    temperature,
    temperatureLowerBound,
    temperatureUpperBound,
    statusCategory,
    activityState: activity,
  } = zoneData || {};

  const zoneStatus = setThermalZoneStatus(zoneData);
  // const isUnmanaged = zoneStatus?.key === THERMAL_ZONE_STATUS.UNMANAGED.key;
  const isInOverride = zoneData?.inOverride || zoneData?.inLocalOverride || false;

  const hvacMode = isInOverride
    ? ACTIVITY.IN_OVERRIDE
    : getHvacMode(zoneData)?.key !== 'auto'
      ? getHvacMode(zoneData)
      : 'unavailable';

  useEffect(() => {
    // console.log('zoneData updated:', zoneData);
    setActiveSchedule(zoneData?.scheduleId);
    setIsKeypadLocked(zoneData?.localOverrideLocked || false);
    setIsManaged(!zoneData?.unmanaged || false);
    setIsOffline(zoneData?.offline || false);
    checkOverrideStatus(zoneData);
  }, [zoneData]);

  useEffect(() => {
    if (!zoneEventData) {
      return;
    }
    setActiveEvents(filterActiveZoneEvents(zoneEventData));
  }, [zoneEventData]);

  useEffect(() => {
    setControlModeEvents(getActiveEventControlModes(activeEvents));
  }, [activeEvents]);

  useEffect(() => {
    if (isLoading) {
      setShowLoading({ message: 'Loading zone data...', isLoading: true });
    } else {
      setShowLoading({ message: '', isLoading: false });
    }
  }, [isLoading]);

  useEffect(() => {
    const combinedControlModes = [
      ...(controlModeOverrides || []),
      ...(controlModeEvents || []),
      ...(thermalZoneControlModes || []),
    ];
    setControlModes([...(combinedControlModes || [])]);
  }, [controlModeOverrides, thermalZoneControlModes, controlModeEvents]);

  useEffect(() => {
    setScheduleOptions(
      scheduleData?.map((schedule) => ({
        label: schedule.scheduleName,
        value: schedule.scheduleId,
      })) || [{ label: 'No schedules available', value: '' }]
    );
  }, [scheduleData]);

  if (!zoneData || isLoading) {
    return <></>;
  }

  function checkOverrideStatus(zoneData) {
    setControlModeOverrides([]);
    setHasRemoteOverride(false);

    if (zoneData?.inLocalOverride && !zoneData?.inOverride) {
      const localOverrideControlMode = {
        id: `localOverride-${zoneData.localOverrideEndTime}`,
        mode: 'LOCAL_OVERRIDE',
        isActive: true,
        duration: {
          startTime: null,
          endTime: zoneData.localOverrideEnds,
        },
        untilDesc: `${zoneData?.localOverrideEnds ? `Until ${new Date(zoneData.localOverrideEnds).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Indefinitely'}`,
        // TODO: decide on more readable date format
        // untilDesc: `${zoneData?.localOverrideEnds ? `Until ${zoneData.localOverrideEnds}` : 'Indefinitely'}`,
        // TODO: Handle when data is available
        // details: `Mode: ${getHvacMode(zoneData)?.label || 'Unavailable'}`,
      };
      setControlModeOverrides([localOverrideControlMode]);
    } else if (zoneData?.inOverride) {
      setHasRemoteOverride(true);
      const remoteOverrideControlMode = {
        id: `override-${zoneData?.overrideEnds ? zoneData.overrideEnds : 'indefinite'}`,
        mode: 'REMOTE_OVERRIDE',
        isActive: true,
        duration: {
          startTime: zoneData?.overrideStarts ? zoneData.overrideStarts : null,
          endTime: zoneData?.overrideEnds ? zoneData.overrideEnds : null,
        },
        // TODO: decide on more readable date format?
        // untilDesc: `${zoneData?.overrideEnds ? `Until ${zoneData.overrideEnds}` : 'Indefinitely'}`,
        untilDesc: `${zoneData?.overrideEnds ? `Until ${new Date(zoneData.overrideEnds).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Indefinitely'}`,
        // TODO: Handle when data is available
        // details: `Mode: ${getHvacMode(zoneData)?.label || 'Unavailable'}`,
        action: () => handleClickCancelOverride(),
        callBackLabel: 'cancel',
      };
      setControlModeOverrides([remoteOverrideControlMode]);
    }
  }

  const renderControlModes = () => {
    const activeControlModes = controlModes.filter((mode) => {
      const targetMode = CONTROL_MODE[mode.mode.toUpperCase()];
      return targetMode && mode.isActive !== false;
    });
    const singleControlMode = activeControlModes && activeControlModes.length === 1;
    if (activeEvents.length === 0 && controlModes.length === 0) {
      return (
        <DataCard
          label="Loading..."
          icon={ICON.ACTIVITY.DEFAULT}
          customClass={`${singleControlMode ? 'single-control-mode' : ''}`}
        />
      );
    }
    if (isOffline) {
      return (
        <DataCard
          label="Offline"
          icon={ACTIVITY.OFFLINE.icon}
          customClass={`${singleControlMode ? 'single-control-mode' : ''}`}
        >
          <div className="until-desc">Currently offline</div>
        </DataCard>
      );
    }
    if (!isManaged) {
      return (
        <DataCard
          label="Zone is Unmanaged"
          icon={ACTIVITY.UNMANAGED.icon}
          customClass={`${singleControlMode ? 'single-control-mode' : ''}`}
        >
          <div className="until-desc">Zone management is disabled</div>
        </DataCard>
      );
    }
    if (!controlModes || controlModes.length === 0) {
      return (
        <DataCard
          label="No Active Control Modes"
          icon={ACTIVITY.DEFAULT.icon}
          customClass={`${singleControlMode ? 'single-control-mode' : ''}`}
        />
      );
    }

    if (activeControlModes.length === 0 && zoneData?.unmanaged === false) {
      return (
        <DataCard
          label={CONTROL_MODE.STANDARD.label}
          icon={CONTROL_MODE.STANDARD.icon}
          customClass={`${singleControlMode ? 'single-control-mode' : ''}`}
        >
          <div className="">
            <div className="">{`Elexity Optimized`}</div>
          </div>
        </DataCard>
      );
    }

    return controlModes.map((controlMode, index) => {
      const targetMode = CONTROL_MODE[controlMode.mode.toUpperCase()];
      if (!targetMode) {
        // console.warn(`Control mode ${controlMode.mode} not found in CONTROL_MODE config`);
        return null;
      }
      if (controlMode.isActive === false) {
        return null;
      }

      const renderDescDetails = () => {
        if (controlMode.mode.toUpperCase() === 'STANDARD') {
          return (
            <div className="">
              <div className="optimized">{`Elexity Optimized`}</div>
            </div>
          );
        }
        return (
          <div>
            {controlMode.untilDesc && <div className="until-desc">{controlMode.untilDesc}</div>}
            {controlMode.details && <div className="details">{controlMode.details}</div>}
            {controlMode.mode.toUpperCase() === 'EVENT' && (
              <div className="optimized">{`Elexity Optimized`}</div>
            )}
          </div>
        );
      };

      return (
        <DataCard
          key={`mode.label-${index}`}
          label={targetMode.label}
          icon={targetMode.icon}
          callBackAction={controlMode.action}
          callBackLabel={controlMode?.callBackLabel || null}
          customLabel={controlMode?.customLabel || null}
        >
          {renderDescDetails()}
        </DataCard>
      );
    });
  };

  const renderHeaderLockup = () => {
    return (
      <>
        <ComfortIcon
          backgroundFill="none"
          fillColor={'#02222B'}
          strokeColor={'#02222B'}
          strokeWidth={2.5}
          width="18px"
        />
        <h1>Comfort</h1>
      </>
    );
  };

  const handleScheduleChange = (scheduleId) => {
    const scheduleName = scheduleData?.find(
      (schedule) => schedule.scheduleId === scheduleId
    )?.scheduleName;
    setShowLoading({ message: 'Updating schedule...', isLoading: true });
    updateThermalZoneSchedule({
      thermalZoneEntityKey: zoneId,
      scheduleName: scheduleName,
      successCallback: () => {
        setShowLoading({ message: '', isLoading: false });
      },
    });
  };

  const handleKeypadLockToggle = (checked) => {
    setIsKeypadLocked(checked);
    setShowLoading({ message: 'Updating lock status...', isLoading: true });
    handleLock({
      lockState: checked,
      thermalZoneEntityKey: zoneId,
      successCallback: () => {
        setTimeout(() => {
          setShowLoading({ message: '', isLoading: false });
          refetchZoneData();
        }, 1000);
      },
      failCallback: () => {
        setTimeout(() => {
          setShowLoading({ message: '', isLoading: false });
          refetchZoneData();
        }, 1000);
      },
    });
  };

  const handleOptimizedToggle = (checked) => {
    openModal('confirmation', {
      modalMessage: `Are you sure you want to set this zone to ${checked === true ? 'managed' : 'unmanaged'}?`,
      modalAction: () => {
        setIsManaged(checked);
        setShowLoading({ message: 'Updating...', isLoading: true });
        handleManageOptimized({
          managedState: checked,
          thermalZoneEntityKey: zoneId,
          successCallback: () => {
            setShowLoading({ message: '', isLoading: false });
          },
        });
      },
    });
  };

  const handleClickCancelOverride = () => {
    openModal('confirmation', {
      modalMessage: `Are you sure you want to cancel the remote override?`,
      modalAction: () => {
        setShowLoading({ message: 'Canceling override...', isLoading: true });
        handleCancelOverride({
          thermalZoneEntityKey: zoneId,
          successCallback: () => {
            setShowLoading({ message: '', isLoading: false });
            refetchZoneData();
            checkOverrideStatus(zoneData);
          },
        });
      },
    });
  };

  const currentScheduleName = scheduleData?.find(
    (schedule) => schedule.scheduleId === activeSchedule
  )?.scheduleName;

  const renderCustomActivityLabel = () => {
    if (activity === ACTIVITY.PROBLEM.key) {
      return `${zoneData.statusCategory ? zoneData.statusCategory : ACTIVITY.PROBLEM.label}`;
    }
    return null;
  };

  return (
    <section className={showMobile ? 'comfort-section mobile' : 'comfort-section desktop'}>
      <Header siteName={campusName} weatherData={campusWeather}>
        <Breadcrumb
          iconHeader={renderHeaderLockup()}
          label={`All Zones`}
          targetPath={`/comfort/${campusId}`}
        />
      </Header>

      <div className="comfort-content comfort-content--detail">
        <div className="content-block ">
          <div className="zone-detail--header">
            <div>
              <div className="header-text--sm">Zone</div>
              <div className="zone-detail--name">
                <h2 className="header-text--main-content">{zoneName}</h2>
                <ThermalZoneStatusPill status={zoneStatus} />
              </div>
            </div>
          </div>

          <section className="zone-detail--temp-activity">
            <div className="temp">
              <div className="header-text--sm">Current Temperature</div>
              <TempSettingsLockup
                status={zoneStatus}
                temperature={temperature}
                tempBounds={{ min: temperatureLowerBound, max: temperatureUpperBound }}
                hvacMode={hvacMode}
                // noBgColor={true}
              />
            </div>
            <div className="activity">
              <div className="header-text--sm">Current Activity</div>
              <ActivityDisplay
                activity={activity.toUpperCase()}
                showLabel={true}
                customLabel={renderCustomActivityLabel()}
              />
            </div>
            <div className="schedule">
              <div className="header-text--sm">
                <span>Assigned Schedule</span>
                {/* TODO: Add the edit schedule functionality */}
                {/* <span className="link" onClick={() => openScheduleModal()}>
                  Edit Schedule
                </span> */}
              </div>
              <div>
                <PermissionBlock
                  readOnlyElement={
                    <div className="activity-label" style={{ marginTop: '18px' }}>
                      {currentScheduleName || 'No schedule assigned'}
                    </div>
                  }
                >
                  <SelectOption
                    options={scheduleOptions}
                    selectedValue={activeSchedule ?? ''}
                    name="zoneSchedule"
                    initialLabel="Select a schedule"
                    onChangeCallback={handleScheduleChange}
                  />
                </PermissionBlock>
              </div>
            </div>
          </section>
          <section className="zone-detail--controls">
            <div className="mode">
              <div className="header-text--sm">Active Control Mode</div>
              <div>{renderControlModes()}</div>
            </div>
            <div className="controls">
              <div className="header-text--sm">Zone Controls</div>
              {/* <div className="controls-container"> */}
              <div className="create-controls">
                <div className="create-controls--buttons">
                  {!hasRemoteOverride ? (
                    <Button
                      label="Create Remote Override"
                      variant="add"
                      onClickCallback={() =>
                        openModal('remote-override', {
                          zoneData,
                          setShowLoading,
                          // errorMessage,
                          // setErrorMessage,
                          errorMessageRef,
                          updateZoneData: refetchZoneData,
                        })
                      }
                    />
                  ) : (
                    <Button
                      label="Cancel Remote Override"
                      variant="cancel"
                      onClickCallback={() => handleClickCancelOverride()}
                    />
                  )}
                  <Button
                    label="Create New Event"
                    variant="add"
                    onClickCallback={() =>
                      openModal('schedule-event', {
                        zoneData,
                        setShowLoading,
                        // errorMessage,
                        // setErrorMessage,
                        errorMessageRef,
                        updateZoneData: refetchZoneEventData,
                      })
                    }
                  />
                </div>
              </div>

              {/* </div> */}
            </div>
            <div className="manage-control">
              <div className="header-text--sm"></div>
              <ToggleWithIcon
                label="Keypad Locked"
                icon={isKeypadLocked ? ICON.LOCK.LOCKED : ICON.LOCK.UNLOCKED}
                onToggle={(checked) => handleKeypadLockToggle(checked)}
                isToggled={isKeypadLocked}
                isReadOnly={isReadOnly}
              />
              <ToggleWithIcon
                label="Elexity Optimized"
                icon={ICON.CONTROL_MODE.ELEXITY}
                onToggle={(checked) => handleOptimizedToggle(checked)}
                isToggled={isManaged}
              />
            </div>
          </section>
        </div>
      </div>
      <div>
        <PerformanceChart
          entityKey={zoneId}
          subtitle={`${zoneName}`}
          graphConfiguration={performanceChartConfiguration}
        />
      </div>
      {(showLoading.isLoading && <Loading message={showLoading.message} />) || null}
    </section>
  );
};

export default ComfortDetail;
